/**
 * ChallengeCard Component
 *
 * Displays a challenge preview card with title, difficulty, and word goal.
 * Used in the Challenge Hub to show available challenges.
 */

import { motion } from 'framer-motion'
import { Target, Star, Sparkles } from 'lucide-react'
import { Link } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { Card, CardContent } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import type { StoryChallenge, ChallengeDifficulty } from '@/types/database'

interface ChallengeCardProps {
  challenge: StoryChallenge
  /** Whether the child has completed this challenge today */
  completedToday?: boolean
  className?: string
}

const DIFFICULTY_CONFIG: Record<
  ChallengeDifficulty,
  { label: string; color: string; bgColor: string; icon: typeof Star }
> = {
  beginner: {
    label: 'Beginner',
    color: 'text-[var(--color-kids-success)]',
    bgColor: 'bg-[var(--color-kids-success)]/10',
    icon: Star,
  },
  intermediate: {
    label: 'Intermediate',
    color: 'text-[var(--color-kids-secondary)]',
    bgColor: 'bg-[var(--color-kids-secondary)]/10',
    icon: Sparkles,
  },
  advanced: {
    label: 'Advanced',
    color: 'text-[var(--color-kids-accent)]',
    bgColor: 'bg-[var(--color-kids-accent)]/10',
    icon: Target,
  },
}

const CATEGORY_EMOJI: Record<string, string> = {
  adventure: '🚀',
  friendship: '🤝',
  fantasy: '✨',
  mystery: '🔍',
  drama: '🎭',
  general: '📖',
}

export function ChallengeCard({
  challenge,
  completedToday = false,
  className,
}: ChallengeCardProps) {
  const difficultyConfig = DIFFICULTY_CONFIG[challenge.difficulty]
  const DifficultyIcon = difficultyConfig.icon
  const categoryEmoji = CATEGORY_EMOJI[challenge.category] || '📖'

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      className={className}
    >
      <Card
        hoverable
        variant={challenge.is_featured ? 'elevated' : 'default'}
        className={cn(
          'relative overflow-hidden',
          challenge.is_featured && 'ring-2 ring-[var(--color-kids-primary)]/30'
        )}
      >
        {/* Featured badge */}
        {challenge.is_featured && (
          <div className="absolute top-0 right-0 bg-[var(--color-kids-primary)] text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
            Featured
          </div>
        )}

        <CardContent className="p-5">
          {/* Header */}
          <div className="flex items-start gap-3 mb-3">
            <span className="text-3xl">{categoryEmoji}</span>
            <div className="flex-1 min-w-0">
              <h3 className="font-display font-bold text-lg text-[var(--color-kids-text)] truncate">
                {challenge.title}
              </h3>
              <div className="flex items-center gap-2 mt-1">
                <span
                  className={cn(
                    'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
                    difficultyConfig.bgColor,
                    difficultyConfig.color
                  )}
                >
                  <DifficultyIcon className="w-3 h-3" />
                  {difficultyConfig.label}
                </span>
                <span className="text-xs text-[var(--color-kids-text-muted)]">
                  {challenge.word_goal_min}–{challenge.word_goal_max} words
                </span>
              </div>
            </div>
          </div>

          {/* Description */}
          <p className="text-sm text-[var(--color-kids-text-muted)] line-clamp-2 mb-4">
            {challenge.description}
          </p>

          {/* Required elements preview */}
          {challenge.required_elements.length > 0 && (
            <div className="mb-4">
              <p className="text-xs text-[var(--color-kids-text-muted)] mb-1">
                Focus on:
              </p>
              <div className="flex flex-wrap gap-1">
                {challenge.required_elements.slice(0, 3).map(element => (
                  <span
                    key={element}
                    className="text-xs bg-[var(--color-kids-surface-alt)] text-[var(--color-kids-text-muted)] px-2 py-0.5 rounded-full"
                  >
                    {element.replace('_', ' ')}
                  </span>
                ))}
                {challenge.required_elements.length > 3 && (
                  <span className="text-xs text-[var(--color-kids-text-muted)]">
                    +{challenge.required_elements.length - 3} more
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Action */}
          <Link to={`/challenge/${challenge.id}`}>
            <Button
              variant={completedToday ? 'secondary' : 'primary'}
              size="sm"
              className="w-full"
              disabled={completedToday}
            >
              {completedToday ? 'Completed Today' : 'Start Challenge'}
            </Button>
          </Link>
        </CardContent>
      </Card>
    </motion.div>
  )
}

/**
 * Compact challenge card for dashboard preview
 */
interface CompactChallengeCardProps {
  challenge: StoryChallenge
  className?: string
}

export function CompactChallengeCard({
  challenge,
  className,
}: CompactChallengeCardProps) {
  const categoryEmoji = CATEGORY_EMOJI[challenge.category] || '📖'
  const difficultyConfig = DIFFICULTY_CONFIG[challenge.difficulty]

  return (
    <Link to={`/challenge/${challenge.id}`}>
      <motion.div
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        className={cn(
          'flex items-center gap-3 p-3 rounded-[var(--radius-kid)]',
          'bg-[var(--color-kids-surface)] shadow-[var(--shadow-kid)]',
          'hover:shadow-[var(--shadow-kid-hover)] transition-all',
          className
        )}
      >
        <span className="text-2xl">{categoryEmoji}</span>
        <div className="flex-1 min-w-0">
          <h4 className="font-display font-semibold text-sm text-[var(--color-kids-text)] truncate">
            {challenge.title}
          </h4>
          <div className="flex items-center gap-2">
            <span
              className={cn(
                'text-xs font-medium',
                difficultyConfig.color
              )}
            >
              {difficultyConfig.label}
            </span>
            <span className="text-xs text-[var(--color-kids-text-muted)]">
              {challenge.word_goal_min}+ words
            </span>
          </div>
        </div>
        <Target className="w-5 h-5 text-[var(--color-kids-primary)]" />
      </motion.div>
    </Link>
  )
}

export default ChallengeCard
